import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Buildings, CaretRight, CaretDown, MagnifyingGlass, ArrowsClockwise, TreeStructure } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const BAND_COLORS = {
  "Individual Contributor": "bg-sky-50 border-sky-200 text-sky-800",
  "Senior IC": "bg-teal-50 border-teal-200 text-teal-800",
  "Manager": "bg-amber-50 border-amber-200 text-amber-800",
  "Senior Manager": "bg-orange-50 border-orange-200 text-orange-800",
  "Director": "bg-purple-50 border-purple-200 text-purple-800",
  "VP": "bg-rose-50 border-rose-200 text-rose-800",
  "C-Suite": "bg-red-50 border-red-200 text-red-800",
};
function bandColor(band) {
  return BAND_COLORS[band] || "bg-zinc-50 border-zinc-200 text-zinc-700";
}

export default function OrgChart() {
  const { user, tenantId } = useAuth();
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [crossTenant, setCrossTenant] = useState(false);

  const load = () => {
    setLoading(true);
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    if (crossTenant && user?.role === "super_admin") params.cross_tenant = 1;
    api.get("/org-chart", { params })
      .then(({ data }) => {
        setTree(data);
        const flatten = (nodes) => nodes.reduce((acc, n) => [...acc, n, ...flatten(n.children || [])], []);
        setFlat(flatten(data));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, crossTenant]);

  const filteredTree = useMemo(() => {
    if (!q) return tree;
    const qq = q.toLowerCase();
    const match = (n) => (n.name || "").toLowerCase().includes(qq) || (n.code || "").toLowerCase().includes(qq) || (n.designation || "").toLowerCase().includes(qq);
    const filter = (nodes) => nodes.reduce((acc, n) => {
      const kids = filter(n.children || []);
      if (match(n) || kids.length) acc.push({ ...n, children: kids });
      return acc;
    }, []);
    return filter(tree);
  }, [tree, q]);

  const bandBuckets = useMemo(() => {
    const buckets = {};
    flat.forEach((n) => {
      const b = n.band || "Unassigned";
      (buckets[b] = buckets[b] || []).push(n);
    });
    return buckets;
  }, [flat]);

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Company · Structure</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Org chart</h1>
          <p className="text-sm text-zinc-500 mt-2">Reporting hierarchy · organized by bands &amp; grades. {flat.length} active employees.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "super_admin" && (
            <label className="inline-flex items-center gap-2 text-xs text-zinc-600 border border-zinc-200 rounded-sm px-2.5 py-1.5 bg-white cursor-pointer hover:border-adb-navy/40" data-testid="org-cross-tenant-toggle">
              <input type="checkbox" checked={crossTenant} onChange={(e) => setCrossTenant(e.target.checked)} className="accent-adb-navy" />
              Cross-tenant view
            </label>
          )}
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
            <Input placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm w-56" data-testid="org-search" />
          </div>
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="org-refresh"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>
      </div>

      <Tabs defaultValue="hierarchy">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="hierarchy" className="rounded-sm" data-testid="org-tab-hierarchy"><TreeStructure size={14} className="mr-1" /> Hierarchy</TabsTrigger>
          <TabsTrigger value="bands" className="rounded-sm" data-testid="org-tab-bands"><Buildings size={14} className="mr-1" /> Bands &amp; grades</TabsTrigger>
        </TabsList>

        <TabsContent value="hierarchy" className="mt-4">
          <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="org-hierarchy">
            {loading && <div className="text-center py-8 text-sm text-zinc-500">Loading tree…</div>}
            {!loading && filteredTree.length === 0 && <div className="text-center py-8 text-sm text-zinc-500">No employees found.</div>}
            {!loading && flattenTree(filteredTree, collapsed).map((n) => (
              <div key={n.id} style={{ marginLeft: n.depth * 20 }} data-testid={`org-node-${n.code}`}>
                <div className="flex items-center gap-2 py-1.5 group">
                  <button onClick={() => n.hasKids && toggle(n.id)} className={`w-5 h-5 grid place-items-center text-zinc-400 hover:text-adb-navy ${!n.hasKids ? "invisible" : ""}`}>
                    {n.collapsed ? <CaretRight size={12} /> : <CaretDown size={12} />}
                  </button>
                  <div className="w-7 h-7 rounded-full bg-adb-navy/10 text-adb-navy grid place-items-center text-[10px] font-bold shrink-0">{(n.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                  <Link to={`/app/employees/${n.id}`} className="flex-1 min-w-0 group-hover:underline">
                    <div className="text-sm">
                      <span className="font-medium">{n.name}</span>
                      <span className="text-zinc-400 ml-2 font-mono-plex text-[11px]">{n.code}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {n.designation || "—"}{n.department && <> · {n.department}</>}
                      {n.dottedManagerName && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-zenith-magenta font-semibold" data-testid={`org-dotted-${n.code}`}>
                          <span className="inline-block w-2 border-t border-dashed border-zenith-magenta"></span>
                          dotted to {n.dottedManagerName}
                          {n.dottedIsCrossTenant && n.dottedManagerTenantName && (
                            <span className="text-zinc-400 font-normal">({n.dottedManagerTenantName})</span>
                          )}
                        </span>
                      )}
                    </div>
                  </Link>
                  {crossTenant && n.tenantName && (
                    <Badge variant="outline" className="rounded-sm text-[10px] hidden sm:inline-flex bg-zenith-teal/10 border-zenith-teal/30 text-zenith-teal-700" data-testid={`org-tenant-${n.code}`}>
                      {n.tenantName}
                    </Badge>
                  )}
                  {n.band && <Badge variant="outline" className={`rounded-sm text-[10px] hidden sm:inline-flex ${bandColor(n.band)}`}>{n.band}</Badge>}
                  {n.grade && <Badge variant="outline" className="rounded-sm text-[10px] font-mono-plex hidden sm:inline-flex">{n.grade}</Badge>}
                  {n.hasKids && <span className="text-[10px] text-zinc-400 font-mono-plex">{n.childCount}</span>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bands" className="mt-4">
          <div className="space-y-4" data-testid="org-bands">
            {Object.entries(bandBuckets)
              .sort(([a], [b]) => (a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b)))
              .map(([band, people]) => (
                <div key={band} className="border border-zinc-200 rounded-sm bg-white">
                  <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`rounded-sm ${bandColor(band)}`}>{band}</Badge>
                      <span className="text-sm text-zinc-500">{people.length} {people.length === 1 ? "member" : "members"}</span>
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {people.map((p) => (
                      <Link to={`/app/employees/${p.id}`} key={p.id} className="border border-zinc-200 rounded-sm p-3 hover:border-adb-navy/40 hover:shadow-sm transition-all bg-white" data-testid={`org-band-card-${p.code}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-adb-navy/10 text-adb-navy grid place-items-center text-xs font-bold shrink-0">{(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-[10px] font-mono-plex text-zinc-500 truncate">{p.code} · {p.designation || "—"}</div>
                          </div>
                          {p.grade && <Badge variant="outline" className="rounded-sm text-[10px] font-mono-plex">{p.grade}</Badge>}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrgNode({ n, depth, collapsed, toggle }) {
  // (unused now — kept for reference; flat rendering used in main JSX)
  return null;
}

function flattenTree(nodes, collapsed, depth = 0, acc = []) {
  for (const n of nodes) {
    const kids = n.children || [];
    const hasKids = kids.length > 0;
    acc.push({
      id: n.id, code: n.code, name: n.name, designation: n.designation,
      department: n.department, band: n.band, grade: n.grade, level: n.level,
      depth, hasKids, childCount: kids.length, collapsed: !!collapsed[n.id],
      dottedManagerName: n.dotted_manager_name,
      dottedManagerTenantName: n.dotted_manager_tenant_name,
      dottedIsCrossTenant: n.dotted_is_cross_tenant,
      tenantName: n.tenant_name,
    });
    if (hasKids && !collapsed[n.id]) flattenTree(kids, collapsed, depth + 1, acc);
  }
  return acc;
}
